import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { useCallback } from 'react';
import { ResourceRequestStatus } from '~/types';
import specificResourceAllocationFragment from '~/modules/resourcing/common/fragments/specificResourceAllocationFragment';
import {
  bulkUpdateResourcePlanSummaryTabCountCache,
  updateResourceRequestsCache
} from '~/modules/resourcing/common/util/resourceRequestUtil';

export const getCacheFunctions = ({
  projectId,
  responseType,
  newRequestStatus,
  oldRequestStatus,
  numberOfRequests
}) => [
  bulkUpdateResourcePlanSummaryTabCountCache({
    responseType,
    newRequestStatus,
    oldRequestStatus,
    numberOfRequests
  }),
  updateResourceRequestsCache({
    projectId,
    responseType
  })
];

const cacheUpdateFunctionsCalls = cacheUpdateFunctions => (
  proxy,
  mutationResponse
) =>
  (cacheUpdateFunctions || []).forEach(func => func(proxy, mutationResponse));

export const BULK_PROPOSE_ALLOCATIONS_FOR_RESOURCE_REQUEST = gql`
  mutation BulkProposeAllocationsForResourceRequest(
    $input: BulkProposeAllocationsForResourceRequestInput!
  ) {
    bulkProposeAllocationsForResourceRequest(input: $input) {
      proposedResourceRequestAndAllocationsEntry {
        resourceRequest {
          id
          requestStatus
        }
        resourceAllocations {
          ...SpecificResourceAllocation
        }
      }
      isOptimistic @client
    }
  }
  ${specificResourceAllocationFragment}
`;

export const useBulkProposeAllocationsForResourceRequest = () => {
  const [
    bulkProposeAllocationsForResourceRequest,
    { loading, error }
  ] = useMutation(BULK_PROPOSE_ALLOCATIONS_FOR_RESOURCE_REQUEST);

  const bulkProposeWithCacheUpdate = useCallback(
    ({ submittedResourceRequestsWithMatches }) => {
      const optimisticResponse = {
        __typename: 'Mutation',
        bulkProposeAllocationsForResourceRequest: {
          __typename: 'BulkProposeAllocationsForResourceRequestResult',
          isOptimistic: true,
          proposedResourceRequestAndAllocationsEntry: submittedResourceRequestsWithMatches.map(
            req => ({
              __typename: 'ProposedResourceRequestAndAllocationsEntry',
              resourceRequest: {
                __typename: 'ResourceRequest',
                id: req.resourceRequest.id,
                requestStatus: ResourceRequestStatus.Tentative
              },
              resourceAllocations: []
            })
          )
        }
      };

      const defaultCacheFunctions = getCacheFunctions({
        projectId: submittedResourceRequestsWithMatches[0].projectUri,
        responseType: 'bulkProposeAllocationsForResourceRequest',
        newRequestStatus: ResourceRequestStatus.Tentative,
        oldRequestStatus: ResourceRequestStatus.Submitted,
        numberOfRequests: submittedResourceRequestsWithMatches.length
      });

      return bulkProposeAllocationsForResourceRequest({
        variables: {
          input: {
            proposedAllocationInput: submittedResourceRequestsWithMatches.map(
              req => ({
                resourceRequestId: req.resourceRequest.id,
                userIds: req.userIds || [],
                ...(req.allocationHoursInDateRangeInput && {
                  allocationHoursInDateRangeInput:
                    req.allocationHoursInDateRangeInput
                })
              })
            )
          }
        },
        refetchQueries: [
          'resourceRequests',
          'resourcePlansSummaryForCurrentTenant'
        ],
        optimisticResponse,
        update: cacheUpdateFunctionsCalls(defaultCacheFunctions)
      });
    },
    [bulkProposeAllocationsForResourceRequest]
  );

  return {
    bulkProposeAllocationsForResourceRequest: bulkProposeWithCacheUpdate,
    isLoading: loading,
    error
  };
};

export default useBulkProposeAllocationsForResourceRequest;
